import { EncryptedSessionStorage } from '@amplify/amp-encrypted-session-storage';
import AuthService from 'lib/authService';
import { setRumAquaId } from 'lib/DDRum';
import * as SearchlightActions from 'a/actions/searchlightActions';
import { setAuthData } from 'a/actions/persistStateActions';
import { GRADES, SEASON } from 'a/hub/data/constants';

import {
  checkLicenses,
  decodeJWT,
  getHasLicenseValues,
  getHasLicenseForGrade,
  getProducts,
  getUserData,
  getUserId,
  getUserAdaptivity,
  postUserLoginTimestamp,
  reportAndRedirectError,
  setStudentData,
  updateLoadingPercentage,
  LOADING_MESSAGES,
} from './loginUtils';
import {
  UNAUTHORIZED_SHARED_LOGIN,
  UNAVAILABLE_MESSAGE,
  UNLICENSED_MESSAGE,
  SUCCESS_MESSAGE,
} from './messages';
import {
  API_LICENSES_KEYS,
  PRODUCT_READING,
  PRODUCT_LECTURA,
  PRODUCT_LECTURA_CAP,
  N_A,
  DEEP_LINK_MODES,
  PROGRAMS,
} from './constants';

const API_TYPES = {
  USER: 'user',
  PLACEMENT_ORIGIN: 'placement origin',
  SEASON: 'season',
};

const USER_TYPES = {
  STAFF: 'staff',
  STUDENT: 'student',
  TEACHER: 'teacher',
};

const setEncryptedItem = async (key, value) => {
  try {
    await EncryptedSessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`setEncryptedItem failed for key: '${key}'`, e);
  }
};

// Helper function to check if API call data is already stored
const getApiData = async (itemName, fetchFn, alwaysFetch = false) => {
  let storedItem; // value if API call was stored in sessionStorage
  let itemResponse; // API call's response
  // check for stored item data after initial login
  if (!AuthService.isInitialLogin) {
    try {
      storedItem = await EncryptedSessionStorage.getItem(itemName);
    } catch (e) {
      // could not identify stored value - get itemResponse from fetchFn
      console.error(`Could not retrieve stored item: '${itemName}'`, e);
    }
  }

  if (storedItem) {
    itemResponse = JSON.parse(storedItem);
  } else {
    try {
      itemResponse = await fetchFn();
      const { status } = itemResponse;
      const body = await itemResponse.json();
      itemResponse = { status, body };

      // if we always need to call an API, don't store data
      if (!alwaysFetch && status === 200) {
        await EncryptedSessionStorage.setItem(itemName, JSON.stringify(itemResponse));
      }
    } catch (e) {
      throw new Error(`Failed ${itemName} API call`, e);
    }
  }

  return itemResponse;
};

const studentLoginEventHandler = (apiVersion, isStaffLogin, sessionId, token, deepLinkConfig) => async dispatch => {
  // initialize searchlight
  dispatch(SearchlightActions.initSearchlightDispatcher(sessionId));

  // decode token
  const decodedToken = await decodeJWT(token);
  if (decodedToken.realm_access.roles.includes('READ_ONLY')) {
    window.location.href = `${window.location.origin}/error.html?message=${UNAUTHORIZED_SHARED_LOGIN}`;
    throw new Error(`Read only role: ${UNAUTHORIZED_SHARED_LOGIN}`);
  }

  // This is a workaround for AQUA-39462:
  // set CryptoKey before running getApiData in parallel to prevent race condition with key generation
  await setEncryptedItem('client', { client: 'aqua-app' });

  updateLoadingPercentage(25, LOADING_MESSAGES.LOADING_STATUS_1);

  // <----------------------------GET USER LICENSE, ID---------------------------->

  // verify the user is licensed to Boost AND get the user's aqua id in parallel
  const [licenseResponse, userIdResponse] = await Promise.all([
    getApiData('licenseResponse', () => checkLicenses(apiVersion, token)),
    getApiData('userIdResponse', () => getUserId(apiVersion, token)),
  ]);

  const { status: licenseStatus, body: { Licenses } } = licenseResponse;
  const { status: userIdStatus, body: { UserId: userId } } = userIdResponse;

  // <----------------------------REDIRECT FOR UNLICENSED USERS---------------------------->

  // check for unauthorized response
  // (this should also handle the case where user logged in using non-mclass
  // credentials--even if they are licensed; see AQUA-22576)
  if ([licenseStatus, userIdStatus].includes(403)) {
    await dispatch(SearchlightActions.reportLoginEvent(UNLICENSED_MESSAGE));
    window.location.href = `${window.location.origin}/error.html?message=${UNLICENSED_MESSAGE}&continue=true`;
    throw new Error(`unauthorized user or license: ${UNLICENSED_MESSAGE}`);
  }

  // check other error response for user id and license call
  if (licenseStatus !== 200 || userIdStatus !== 200) {
    await dispatch(SearchlightActions.reportLoginEvent(UNAVAILABLE_MESSAGE));
    window.location.href = `${window.location.origin}/error.html?message=${UNAVAILABLE_MESSAGE}`;
    throw new Error(`error with user or license: ${UNAVAILABLE_MESSAGE}`);
  }

  // set userId in DDRum and Searchlight
  setRumAquaId(userId);
  dispatch(SearchlightActions.setUserId(userId));
  updateLoadingPercentage(50, LOADING_MESSAGES.LOADING_STATUS_2);

  // <----------------------------GET USER DATA, PLACEMENT, SEASON---------------------------->
  // example licenses response
  // {
  //   "Licenses": {
  //     "Reading": {
  //       "HasBoostCkla3e": false,
  //       "HasBoostEla3e": false,
  //       "LicenseType": "Standard",
  //       "LicensedGrades": ["KG"],
  //       "SpecialContentLicense": "false",
  //     },
  //     "Lectura": {
  //       "HasBoostCaminos3e": false,
  //       "LicenseType": null,
  //       "LicensedGrades": ["KG"],
  //       "SpecialContentLicense": "false",
  //     },
  //     "Math": {  --> no longer used
  //       "LicenseType": "Standard",
  //       "SpecialContentLicense": false
  //     }
  //   }
  // }

  // check available licenses - Reading, Lectura (or both) & flag for CKLA or ELA
  const [hasReadingLicense, hasLecturaLicense, hasCklaLicense, hasElaLicense, hasCaminosLicense] = getHasLicenseValues(Licenses);

  // no valid license to any Boost product
  if (!hasReadingLicense && !hasLecturaLicense && !hasCklaLicense && !hasElaLicense && !hasCaminosLicense) {
    await dispatch(SearchlightActions.reportLoginEvent(UNLICENSED_MESSAGE));
    window.location.href = `${window.location.origin}/error.html?message=${UNLICENSED_MESSAGE}&continue=true`;
    throw new Error(`unauthorized license: ${UNLICENSED_MESSAGE}`);
  }

  // if student is coming from deep link, skip unnecessary API calls
  // if student has CKLA or ELA license, allow access without standard Reading license
  // if student has Caminos license, allow access without standard Lectura license
  const { product: deepLinkProduct, mode: deepLinkMode } = deepLinkConfig || {};
  const isDeepLink = Object.keys(deepLinkConfig).length > 0;
  const isAssignmentLink = deepLinkMode === DEEP_LINK_MODES.ASSIGNMENT;
  const isCklaDeepLink = isAssignmentLink && hasCklaLicense;
  const isElaDeepLink = isAssignmentLink && hasElaLicense;
  const isCaminosDeepLink = isAssignmentLink && hasCaminosLicense;
  const isAssignmentLinkReading = isCklaDeepLink || isElaDeepLink;
  const isAssignmentLinkLectura = isCaminosDeepLink;

  let canUseReading = false;
  let canUseLectura = false;

  // Get primary user data
  const [userResponseReading, userResponseLectura] = await Promise.all([
    getApiData('userResponseReading', () => (
      hasReadingLicense || isAssignmentLinkReading
        ? getUserData(apiVersion, token, userId, PRODUCT_READING, isAssignmentLinkReading, dispatch)
        : Promise.resolve({ status: 403, json() { } })
    )),
    getApiData('userResponseLectura', () => (
      hasLecturaLicense || isAssignmentLinkLectura
        ? getUserData(apiVersion, token, userId, PRODUCT_LECTURA, isAssignmentLinkLectura, dispatch)
        : Promise.resolve({ status: 403, json() { } })
    )),
  ]);

  const { status: userStatusReading, body: userResponseBodyReading } = userResponseReading;
  const { status: userStatusLectura, body: userResponseBodyLectura } = userResponseLectura;

  const [hasReadingLicenseForGrade, hasLecturaLicenseForGrade] =
    getHasLicenseForGrade(Licenses, userResponseBodyReading?.Grade, userResponseBodyLectura?.Grade);

  // Check Grade from userResponse since license is based on institution but access can vary by individual/grade.
  // We can have an institution licensed to Reading and Lectura but not for all grades.
  // (eg, Reading license for grades KG, 01, Lectura license for grades KG, 01, 02)
  canUseReading = isAssignmentLinkReading || (
    hasReadingLicense &&
    (hasReadingLicenseForGrade || isStaffLogin || userResponseBodyReading?.UserType === USER_TYPES.STAFF)
  );
  canUseLectura = isAssignmentLinkLectura || (
    hasLecturaLicense &&
    (hasLecturaLicenseForGrade || isStaffLogin || userResponseBodyLectura?.UserType === USER_TYPES.STAFF)
  );

  // Get adaptivity data (placementOrigin (including TOY) and season) for Reading and Lectura separately
  const adaptivityResponseReading = await getApiData('adaptivityResponseReading', () =>
    (canUseReading && !isDeepLink
      ? getUserAdaptivity(apiVersion, token, PRODUCT_READING, dispatch)
      : Promise.resolve({ status: 403, json() { } })
    ), true, // always make call in case of updates
  );

  const adaptivityResponseLectura = await getApiData('adaptivityResponseLectura', () =>
    (canUseLectura && !isDeepLink
      ? getUserAdaptivity(apiVersion, token, PRODUCT_LECTURA, dispatch)
      : Promise.resolve({ status: 403, json() { } })
    ), true, // always make call in case of updates
  );

  const { status: adaptivityStatusReading, body: adaptivityResponseBodyReading } = adaptivityResponseReading;
  const { status: adaptivityStatusLectura, body: adaptivityResponseBodyLectura } = adaptivityResponseLectura;

  let userResponseBody = {}; // user data
  const originData = {}; // placement origin data (BOY/MOY/EOY assessments)

  // <----------------------------HELPERS---------------------------->

  const getOverriddenField = (originalField, booleanOverride) => {
    /**
     * Override boolean fields in the user data response if we have a manual override.
     */
    if (booleanOverride) {
      if (booleanOverride === 'true') {
        return true;
      }

      if (booleanOverride === 'false') {
        return false;
      }
      return booleanOverride;
    }
    return originalField;
  };

  const setPlacementOriginFields = (response = {}, product) => {
    const { placementOrigin, season } = response;
    const seasonData = season ?? {};
    let extra = '';
    if (product === PRODUCT_LECTURA) {
      seasonData.season = [GRADES.GRADE_2, GRADES.GRADE_3].includes(userResponseBodyLectura.Grade)
        ? SEASON.GRADE_2_3
        : SEASON.GRADE_K_1;
      seasonData.part = 'A';
      extra = PRODUCT_LECTURA_CAP;
    }

    // session storage values for placement (used for QA/dev):
    const manualIsFirstLogin = sessionStorage.getItem(`isFirstLogin${extra}`); // true/false
    const manualRedirect = sessionStorage.getItem(`redirectToIpt${extra}`); // true/false
    const manualRedirectType = sessionStorage.getItem(`iptType${extra}`); // APT/TRO-APT
    const manualTOY = sessionStorage.getItem(`toy${extra}`); // BOY, MOY, EOY

    originData[`isFirstLogin${extra}`] = getOverriddenField(placementOrigin.isFirstLogin, manualIsFirstLogin);
    originData[`RedirectToIPT${extra}`] = getOverriddenField(placementOrigin.RedirectToIPT, manualRedirect);
    originData[`IPTType${extra}`] = getOverriddenField(placementOrigin.IPTType, manualRedirectType);
    originData[`placedMethod${extra}`] = placementOrigin.placedMethod;
    originData[`placementPeriod${extra}`] = placementOrigin.placementPeriod;
    originData[`placementGrade${extra}`] = placementOrigin.placementGrade;
    originData[`skillMap${extra}`] = placementOrigin.skillMap;

    originData[`toy${extra}`] = getOverriddenField(placementOrigin.toy, manualTOY);
    originData[`Season${extra}`] = seasonData?.season;
    originData[`Part${extra}`] = seasonData?.part;
  };

  const isApiResponseSuccessful = (status) => status === 200;

  const handleApiResponseStatus = async (apiType, status, responseBody = {}) => {
    if (apiType === API_TYPES.USER && status === 403) {
      const { ErrorCode } = responseBody;
      await reportAndRedirectError(ErrorCode, dispatch);
      throw new Error(`${apiType} API response status ${status}: ${JSON.stringify(responseBody)}`);
    }

    if (status !== 200) {
      await dispatch(SearchlightActions.reportLoginEvent(UNAVAILABLE_MESSAGE));
      window.location.href = `${window.location.origin}/error.html?message=${UNAVAILABLE_MESSAGE}`;
      throw new Error(`${apiType} API response status ${status}`);
    }

    return isApiResponseSuccessful(status);
  };

  // <----------------------------CHECK API RESPONSES: REDIRECT ON ERROR---------------------------->

  const checkAPIsByLicense = async () => {
    // if licensed to both Reading and Lectura
    if (canUseReading && canUseLectura) {
      // default to reading response for other user info if licensed to both due to overlap of data
      userResponseBody = userResponseBodyReading;

      // check placement response status - if both not 200, report error
      await Promise.all([
        handleApiResponseStatus(API_TYPES.PLACEMENT_ORIGIN, adaptivityStatusReading),
        handleApiResponseStatus(API_TYPES.PLACEMENT_ORIGIN, adaptivityStatusLectura),
      ]);
    } else if (canUseReading) { // Reading only license
      canUseReading = await handleApiResponseStatus(API_TYPES.USER, userStatusReading, userResponseBodyReading);
      userResponseBody = userResponseBodyReading;

      await handleApiResponseStatus(API_TYPES.PLACEMENT_ORIGIN, adaptivityStatusReading);
    } else if (canUseLectura) { // Lectura only license
      canUseLectura = await handleApiResponseStatus(API_TYPES.USER, userStatusLectura, userResponseBodyLectura);
      userResponseBody = userResponseBodyLectura;

      await handleApiResponseStatus(API_TYPES.PLACEMENT_ORIGIN, adaptivityStatusLectura);
    } else if (hasReadingLicense || hasLecturaLicense) {
      // student licensed to product but not grade and there was an API issue
      const userStatus = hasReadingLicense ? userStatusReading : userStatusLectura;
      userResponseBody = hasReadingLicense ? userResponseBodyReading : userResponseBodyLectura;
      await handleApiResponseStatus(API_TYPES.USER, userStatus, userResponseBody);
    } else { // student is not licensed to Reading or Lectura
      await dispatch(SearchlightActions.reportLoginEvent(UNLICENSED_MESSAGE));
      window.location.href = `${window.location.origin}/error.html?message=${UNLICENSED_MESSAGE}&continue=true`;
      throw new Error(`unauthorized license: ${UNLICENSED_MESSAGE}`);
    }
  };

  updateLoadingPercentage(75, LOADING_MESSAGES.LOADING_STATUS_3);

  if (isDeepLink) {
    if (canUseReading && deepLinkProduct === PRODUCT_READING) {
      canUseReading = await handleApiResponseStatus(API_TYPES.USER, userStatusReading, userResponseBodyReading);
      userResponseBody = userResponseBodyReading;
    } else if (canUseLectura && deepLinkProduct === PRODUCT_LECTURA) {
      canUseLectura = await handleApiResponseStatus(API_TYPES.USER, userStatusLectura, userResponseBodyLectura);
      userResponseBody = userResponseBodyLectura;
    } else {
      await dispatch(SearchlightActions.reportLoginEvent(UNLICENSED_MESSAGE));
      window.location.href = `${window.location.origin}/error.html?message=${UNLICENSED_MESSAGE}&continue=true`;
      throw new Error(`unauthorized license for deep link product ${deepLinkProduct}: ${UNLICENSED_MESSAGE}`);
    }
    // set default values for deep links
    if (isAssignmentLink && !userResponseBody.Grade) {
      userResponseBody.Grade = N_A;
    }
    originData.Season = N_A;
    originData.Part = N_A;
    originData.skillMap = `${deepLinkProduct}-default`;
  } else {
    await checkAPIsByLicense();
    // update originData object based on placement response
    if (canUseReading) {
      setPlacementOriginFields(adaptivityResponseBodyReading, PRODUCT_READING);
    }
    if (canUseLectura) {
      setPlacementOriginFields(adaptivityResponseBodyLectura, PRODUCT_LECTURA);
    }
  }

  // <--------------------SUCCESSFUL API RESPONSES: SET AUTH DATA AND LOAD APP-------------------->

  // it's possible that we are auto-logging in for the user because they are
  // already logged into Keycloak, but they could be a staff user licensed to
  // Boost Reading who is not in the student app in another tab/window.
  let userType = isStaffLogin ? USER_TYPES.TEACHER : USER_TYPES.STUDENT;
  if (!isStaffLogin && userResponseBody.UserType === USER_TYPES.STAFF) {
    userType = USER_TYPES.TEACHER;
  }
  // manually add a valid grade & season value for staff to not break Searchlight event calls
  if (userType === USER_TYPES.TEACHER) {
    userResponseBody.Grade = N_A;
    originData.Season = N_A;
    originData.Part = N_A;
    originData.SeasonLectura = N_A;
    originData.PartLectura = N_A;
  }
  // manually add in the IsStaffLogin field
  userResponseBody.IsStaffLogin = userType === USER_TYPES.TEACHER;

  const getDeepLinkProgram = () => {
    let program = null;
    if (isDeepLink) {
      if (isCklaDeepLink) {
        program = PROGRAMS.CKLA;
      } else if (isElaDeepLink) {
        program = PROGRAMS.ELA;
      } else if (isCaminosDeepLink) {
        program = PROGRAMS.CAMINOS;
      } else {
        program = deepLinkProduct === PRODUCT_READING ? PROGRAMS.PRODUCT_READING : PROGRAMS.PRODUCT_LECTURA;
      }
    }
    return program;
  };

  // successful API calls - update identity object in Searchlight
  const identity = {
    studentId: `${userResponseBody.UserId}`,
    organization: userResponseBody.Organization || '',
    group: userResponseBody?.Groups?.toString() ?? '',
    grade: userResponseBody.Grade,
    isTestUser: typeof userResponseBody.IsTestUser === 'string'
      ? userResponseBody.IsTestUser.toLowerCase() === 'true'
      : userResponseBody.IsTestUser,
    userType,
    isDeepLink,
    deepLinkProgram: getDeepLinkProgram(),
  };
  dispatch(SearchlightActions.setIdentity(identity));

  // only send login reports on initial login - not refresh/new tab
  if (AuthService.isInitialLogin) {
    AuthService.storeLoginMetadata();
    dispatch(SearchlightActions.reportLoginEvent(SUCCESS_MESSAGE, { ...userResponseBody, userType }));

    // student-only login calls
    if (userType === USER_TYPES.STUDENT) {
      // create login entry in the user_login table
      await postUserLoginTimestamp(token);
    }
  }

  // Check if license is a field study license
  userResponseBody.productsAvailable = await getProducts(canUseReading, canUseLectura, userId, token);
  const isFieldTrial = Licenses[API_LICENSES_KEYS.LECTURA].LicenseType === 'Trial';
  userResponseBody.fieldTrial = isFieldTrial;
  if (isFieldTrial) {
    dispatch(SearchlightActions.setSourceId(isFieldTrial));
  }

  // Check SpecialContentLicense license
  userResponseBody.IsSpecialContentLicense = Licenses[API_LICENSES_KEYS.READING].SpecialContentLicense;

  // Set all auth data from login to persistState reducer
  const authData = setStudentData({ ...userResponseBody, ...originData });
  await dispatch(setAuthData(authData));
  updateLoadingPercentage(95, LOADING_MESSAGES.LOADING_STATUS_4);
};

export default studentLoginEventHandler;
